import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import ProposalEditor from "../../components/proposal/ProposalEditor";

const API_URL = import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";

const IMDFEditorEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchIMDFData = async () => {
      if (!id) {
        setError("Missing IMDF file ID");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        console.log("Fetching IMDF file:", id);

        // 下載 ZIP 檔案
        const response = await fetch(`${API_URL}/imdf/files/${id}/download`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to download IMDF file: ${response.status}`);
        }

        const blob = await response.blob();
        console.log("Downloaded blob size:", blob.size);

        // 解壓縮 ZIP 檔案
        const zip = new JSZip();
        const zipFile = await zip.loadAsync(blob);
        console.log("ZIP contents:", Object.keys(zipFile.files));

        const allFeatures: GeoJSON.Feature[] = [];

        // 處理 ZIP 中的每個 GeoJSON 檔案
        for (const [fileName, file] of Object.entries(zipFile.files)) {
          if (file.dir) continue;

          const fileExtension = fileName.split(".").pop()?.toLowerCase();

          if (fileExtension === "geojson") {
            try {
              const content = await file.async("text");
              const geoJson = JSON.parse(content);

              const features = geoJson.features || [];

              // 為每個 feature 添加檔案來源資訊
              const enhancedFeatures = features.map((feature: GeoJSON.Feature) => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  _sourceFile: fileName,
                  _file_type: fileName.split(".")[0],
                },
              }));

              allFeatures.push(...enhancedFeatures);
              console.log(`Processed ${fileName}: ${features.length} features`);
            } catch (err) {
              console.error(`Failed to parse ${fileName}:`, err);
            }
          }
        }

        // 創建合併的 GeoJSON FeatureCollection
        const combinedGeoJson: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features: allFeatures,
        };

        console.log("Total features loaded:", allFeatures.length);
        setGeoJsonData(combinedGeoJson);
      } catch (err) {
        console.error("Error loading IMDF data:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchIMDFData();
  }, [id]);

  const handleCancel = () => {
    navigate("/imdf-editor");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !geoJsonData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">載入錯誤</h3>
          <p className="mt-1 text-sm text-gray-500">{error || "Failed to load IMDF data"}</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProposalEditor
      imdfFileId={id!}
      geoJsonData={geoJsonData}
      onCancel={handleCancel}
    />
  );
};

export default IMDFEditorEditPage;
